import React, { FC } from 'react';
import styled from 'styled-components';
import { DocumentsAPI } from 'api/document/document-core';
import { Document as DocumentData } from 'api/document/types/document';
import { UserType } from 'api/models';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { EditorsObject } from 'components/organisms/ck-collab-editor';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { useUser } from 'hooks/use-user';
import { useUserType } from 'hooks/use-user-type';
import { DocumentFreezeCommand } from 'vendor/ckeditor/plugins/freeze-document';
import { FreezeAction } from '../../document.types';
import { AskForApprovalBtn } from '../ask-for-approval';
import { ExportToPdfBtn } from '../export-to-pdf';
import { FreezeDocumentBtn } from '../freeze-document';
import { FreezeToolbar } from '../freeze-toolbar';

interface Props {
  onFreezeToggle: (arg: FreezeAction) => void;
  onApprovalBtnClick: () => void;
  onExportPdfBtnClick: () => void;
  exportPdfloading: boolean;
  docFreeze: FreezeAction;
  allEditors: EditorsObject;
  projectId: number;
  documentId: number;
}

const Actions = styled.div`
  display: flex;
  .ant-btn {
    margin-right: 2px;
  }
`;

export const CollabActions: FC<Props> = ({
  onFreezeToggle,
  onApprovalBtnClick,
  onExportPdfBtnClick,
  exportPdfloading,
  docFreeze,
  allEditors,
  projectId,
  documentId,
}) => {
  const handleFreezeAction = async ({
    enableFreeze,
    updateApi,
    frozenAt,
    frozenBy,
  }: {
    enableFreeze: boolean;
    updateApi?: boolean;
    frozenAt?: string;
    frozenBy?: number;
  }) => {
    const command = enableFreeze
      ? DocumentFreezeCommand.Freeze
      : DocumentFreezeCommand.UnFreeze;

    allEditors.PUBLIC?.execute(command);
    allEditors.PRIVATE?.execute(command);
    if (updateApi) {
      const resp = await DocumentsAPI.updateDocumentFreeze(
        projectId,
        documentId,
        enableFreeze,
      );

      if (enableFreeze) {
        onFreezeToggle({
          frozen: enableFreeze,
          frozenAt: resp.frozenAt,
          frozenBy: resp.frozenBy,
        });
      }
    }

    onFreezeToggle({
      frozen: enableFreeze,
      frozenAt,
      frozenBy,
    });
  };

  const userType = useUserType();
  const isUserNative =
    userType === UserType.Native || userType === UserType.Partner;

  const handleSocketUpdate = (socketData: SocketData<DocumentData>) => {
    if (
      socketData.data.documentId === documentId &&
      socketData.data?.frozen !== undefined &&
      docFreeze.frozen !== socketData.data?.frozen
    ) {
      handleFreezeAction({
        enableFreeze: !!socketData.data?.frozen,
        frozenAt: socketData.data?.frozenAt,
        frozenBy: socketData.data?.frozenBy,
      });
    }
  };

  useProjectWSUpdates(
    projectId,
    handleSocketUpdate,
    EventType(Action.Update, ObjectType.Document),
  );

  const { user: currentUser } = useUser();

  return docFreeze.frozen ? (
    <FreezeToolbar
      hideApprovalBtn={!isUserNative}
      docFreeze={docFreeze}
      onUnfreeze={() =>
        handleFreezeAction({
          enableFreeze: false,
          updateApi: true,
        })
      }
      onReqApproval={() => {
        onApprovalBtnClick();
      }}
    />
  ) : (
    <Actions>
      <FreezeDocumentBtn
        onClick={() =>
          handleFreezeAction({
            enableFreeze: true,
            updateApi: true,
            frozenBy: currentUser.userId,
          })
        }
      />
      {isUserNative ? (
        <>
          <AskForApprovalBtn onClick={onApprovalBtnClick} />
        </>
      ) : null}
      <ExportToPdfBtn
        onClick={onExportPdfBtnClick}
        loading={exportPdfloading}
      />
    </Actions>
  );
};
